import { useState, useEffect, useId, useMemo } from "react";
import { Button, Modal, Nav, Tab } from "react-bootstrap";

import { api_without_cancellation } from "../../../api/api";
import { getCaseId, getClientId } from "../../../Utils/helper";
import { notifyPageAttachmentsChanged } from "../../../Utils/pageAttachmentEvents";

const UploadPhotoModal = ({
  show,
  handleClose,
  selectedSlot,
  photoSlots = [],
  refetch,
  isPanel = false,
  panelId = "",
  pageName = "",
}) => {
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [selectedExisting, setSelectedExisting] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedPreviewUrl, setUploadedPreviewUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeSlotId, setActiveSlotId] = useState(selectedSlot?.id);
  const navigationId = useId();
  const slots = useMemo(() => {
    const selectedPageId = selectedSlot?.page?.id ?? selectedSlot?.page;
    const seen = new Set();
    return [...photoSlots, selectedSlot]
      .flatMap((item) => {
        const slot = item?.photo_slot || item;
        const pageId = slot?.page?.id ?? slot?.page ?? selectedPageId;
        if (
          !slot?.id ||
          seen.has(String(slot.id)) ||
          String(pageId) !== String(selectedPageId) ||
          (isPanel &&
            item?.panel_id !== undefined &&
            item.panel_id !== null &&
            String(item.panel_id) !== String(panelId))
        )
          return [];
        seen.add(String(slot.id));
        return [{ ...slot, page: pageId, page_name: slot.page?.name || slot.page_name }];
      })
      .sort(
        (first, second) => (Number(first.slot_number) || 0) - (Number(second.slot_number) || 0)
      );
  }, [photoSlots, selectedSlot, isPanel, panelId]);
  const activeSlot = slots.find((slot) => String(slot.id) === String(activeSlotId)) || slots[0];
  const getSlotLabel = (slot, index) => {
    const name = (slot?.page_name || pageName).trim().replace(/\s+Page$/i, "");
    return `${name ? `${name} Page` : "Page"} Photo ${index + 1}`;
  };

  useEffect(() => {
    if (show) {
      setActiveSlotId(selectedSlot?.id);
      setSelectedExisting(null);
      setUploadedFile(null);
      fetchExistingPhotos();
    }
  }, [show, selectedSlot?.id]);

  useEffect(() => {
    if (!uploadedFile || !show) {
      setUploadedPreviewUrl("");
      return;
    }
    const previewUrl = URL.createObjectURL(uploadedFile);
    setUploadedPreviewUrl(previewUrl);
    return () => URL.revokeObjectURL(previewUrl);
  }, [uploadedFile, show]);

  const handleSlotChange = (slotId) => {
    if (String(slotId) === String(activeSlot?.id)) return;
    setActiveSlotId(slotId);
    setSelectedExisting(null);
    setUploadedFile(null);
  };

  const handleTabKeyDown = (event, index) => {
    const directions = {
      ArrowRight: 1,
      ArrowLeft: -1,
      Home: -index,
      End: slots.length - 1 - index,
    };
    if (!(event.key in directions)) return;
    event.preventDefault();
    event.stopPropagation();
    const nextIndex = (index + directions[event.key] + slots.length) % slots.length;
    handleSlotChange(slots[nextIndex].id);
    event.currentTarget.parentElement.querySelectorAll("[role=tab]")[nextIndex].focus();
  };

  const fetchExistingPhotos = async () => {
    setLoading(true);
    try {
      const response = await api_without_cancellation.get(
        `/api/photo/list-all/${getClientId()}/${getCaseId()}/`
      );
      const data = await response.data;
      setExistingPhotos(Array.isArray(data?.data) ? data.data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectExisting = (photo) => {
    if (selectedExisting?.id === photo.id) {
      setSelectedExisting(null);
    } else {
      setSelectedExisting(photo);
      setUploadedFile(null);
    }
  };

  const handleFileChange = (e) => {
    if (selectedExisting) return;
    setUploadedFile(e.target.files[0]);
  };

  const handleSave = async () => {
    if (!activeSlot?.id) return;
    const pageNameToModelName = {
      treatment: "CaseProviders",
      defendant: "Defendant",
      defendants: "Defendant",
      witnesses: "Witness",
      insurance: "Insurance",
      parties: "OtherParty",
      reports: "Reports",
      employment: "Employment",
      loans: "CaseLoan",
      discovery: "Discovery",
      recording: "Recordings",
      experts: "CaseExpert",
      Litigation: "LitigationAct",
    };
    const modelName = pageNameToModelName[pageName?.toLowerCase()] || "";
    if (selectedExisting) {
      try {
        const response = await api_without_cancellation.post("/api/attach_photo_to_page/", {
          page_id: activeSlot?.page,
          photo_id: selectedExisting?.id,
          slot_id: activeSlot?.id,
          case_id: getCaseId(),
          client_id: getClientId(),
          ...(isPanel ? { panel_id: panelId, model_name: modelName } : {}),
          // panel_id: "",
          // model_name: ""
        });
        if (response.status === 200 || response.status === 201) {
          notifyPageAttachmentsChanged(getCaseId(), activeSlot?.page, "photos");
          handleClose();
          refetch();
        }
      } catch (error) {
        console.error(error);
      }
    } else if (uploadedFile) {
      const formData = new FormData();
      formData.append("file", uploadedFile);
      try {
        const response = await api_without_cancellation.post(
          `/api/upload_photo/${getClientId()}/${getCaseId()}/`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
        if (response.status === 200 || response.status === 201) {
          const data = await response.data;
          const response2 = await api_without_cancellation.post("/api/attach_photo_to_page/", {
            page_id: activeSlot?.page,
            photo_id: data?.doc?.id,
            slot_id: activeSlot?.id,
            case_id: getCaseId(),
            client_id: getClientId(),
            ...(isPanel ? { panel_id: panelId, model_name: modelName } : {}),
            // panel_id: "",
            // model_name: ""
          });
          if (response2.status === 200 || response2.status === 201) {
            notifyPageAttachmentsChanged(getCaseId(), activeSlot?.page, "photos");
            handleClose();
            refetch();
          }
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  const isDisabled = !!selectedExisting;

  return (
    <Modal
      show={show}
      onHide={handleClose}
      tabIndex={-1}
      role="dialog"
      dialogClassName="max-1050 modal-dialog-centered"
      contentClassName="custom-modal-new-provider"
      size="lg"
      centered
    >
      <div>
        <Modal.Header
          style={{ borderBottom: "0px" }}
          className="text-center height-25 p-0 bg-primary popup-heading-color justify-content-center"
        >
          <Modal.Title
            className="mx-auto height-25 font-weight-semibold text-uppercase popup-heading-color d-flex align-items-center"
            style={{ fontSize: "14px", fontWeight: "600" }}
          >
            Upload Photo — {activeSlot?.slot_name}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body
          className="d-flex flex-column"
          style={{
            padding: "0px",
            height: "600px",
            maxHeight: "calc(100dvh - 130px)",
            overflow: "hidden",
          }}
        >
          {slots.length > 1 && (
            <div
              className="custom-tab m-t-5 flex-shrink-0"
              data-testid="photo-upload-slot-navigation"
            >
              <Tab.Container
                activeKey={String(activeSlot?.id)}
                onSelect={handleSlotChange}
                generateChildId={(key, type) =>
                  type === "tab" ? `${navigationId}-slot-${key}` : `${navigationId}-panel`
                }
              >
                <Nav
                  variant="tabs"
                  className="justify-content-around m-t-5 m-b-5"
                  role="tablist"
                  aria-label="Page photo slots"
                >
                  {slots.map((slot, index) => (
                    <Nav.Link
                      key={slot.id}
                      eventKey={String(slot.id)}
                      id={`${navigationId}-slot-${slot.id}`}
                      role="tab"
                      className={`new-provider-nav-link-hover-effect nav-link Pad8 tab-ite${index === 0 ? " apply-before" : index === slots.length - 1 ? " apply-after" : ""}`}
                      aria-controls={`${navigationId}-panel`}
                      onKeyDown={(event) => handleTabKeyDown(event, index)}
                    >
                      {getSlotLabel(slot, index)}
                    </Nav.Link>
                  ))}
                </Nav>
              </Tab.Container>
            </div>
          )}
          {slots.length === 1 && (
            <div
              className="bg-primary text-white height-25 d-flex align-items-center justify-content-center fw-semibold m-t-5 m-b-5 flex-shrink-0"
              id={`${navigationId}-single-slot`}
              data-testid="photo-upload-slot-bar"
            >
              {getSlotLabel(slots[0], 0)}
            </div>
          )}
          <div
            id={`${navigationId}-panel`}
            className="d-flex flex-column"
            style={{ flex: "1 1 0", minHeight: 0, overflow: "hidden" }}
            role={slots.length > 1 ? "tabpanel" : undefined}
            aria-labelledby={
              slots.length > 1
                ? `${navigationId}-slot-${activeSlot?.id}`
                : slots.length === 1
                  ? `${navigationId}-single-slot`
                  : undefined
            }
          >
            {loading ? (
              <div className="text-center py-4">Loading...</div>
            ) : (
              <>
                {existingPhotos.length > 0 ? (
                  <div
                    className="m-b-5 p-l-5 p-r-5 d-flex flex-column"
                    style={{ flex: "0 1 auto", minHeight: 0 }}
                  >
                    <p
                      className="m-b-5 text-uppercase font-weight-semibold flex-shrink-0"
                      style={{ fontSize: "14px", color: "var(--primary-25)" }}
                    >
                      Already Uploaded Photos
                    </p>
                    <div
                      className="d-flex flex-wrap invisible-scrollbar"
                      data-testid="uploaded-photo-list"
                      style={{
                        gap: "5px",
                        flex: "0 1 auto",
                        minHeight: 0,
                        overflowY: "auto",
                        alignContent: "flex-start",
                      }}
                    >
                      {existingPhotos.map((photo) => {
                        const isSelected = selectedExisting?.id === photo.id;
                        return (
                          <div
                            key={photo.id}
                            onClick={() => handleSelectExisting(photo)}
                            style={{
                              width: "255px",
                              height: "155px",
                              flexShrink: 0,
                              cursor: "pointer",
                              border: isSelected
                                ? "2px solid var(--primary)"
                                : "2px solid var(--primary-25)",
                              opacity: selectedExisting && !isSelected ? 0.4 : 1,
                              position: "relative",
                              transition: "opacity 0.2s, border 0.2s",
                            }}
                          >
                            <img
                              src={photo.image}
                              alt={photo.title}
                              style={{ width: "100%", height: "100%", objectFit: "contain" }}
                            />
                            {isSelected && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: "4px",
                                  right: "4px",
                                  background: "var(--primary-25)",
                                  borderRadius: "50%",
                                  width: "18px",
                                  height: "18px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <i
                                  className="fas fa-check"
                                  style={{ fontSize: "9px", color: "white" }}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="m-b-5 flex-shrink-0">
                    <div
                      className="text-uppercase d-flex align-items-center justify-content-center font-weight-semibold"
                      style={{
                        fontSize: "14px",
                        color: "var(--primary)",
                        background: "var(--primary-25)",
                        height: "35px",
                      }}
                    >
                      No Photos Uploaded on Case
                    </div>
                  </div>
                )}

                <div
                  className="row mx-0 p-l-5 p-r-5 p-t-5 p-b-5 flex-shrink-0"
                  data-testid="upload-from-computer"
                >
                  <div className="col-3 p-0">
                    <p
                      className="m-b-5 text-uppercase font-weight-semibold"
                      style={{ fontSize: "14px", color: "var(--primary-25)" }}
                    >
                      Upload from Computer
                    </p>

                    <input
                      key={activeSlot?.id}
                      type="file"
                      accept="image/*"
                      disabled={isDisabled}
                      onChange={handleFileChange}
                      id="photo-upload-input"
                      style={{ display: "none" }}
                    />

                    <div className="d-flex justify-content-start">
                      <label
                        htmlFor="photo-upload-input"
                        className={`btn btn-primary height-25 mb-0 d-flex align-items-center button-padding-footer-new-provider ${isDisabled ? "disabled" : ""}`}
                        style={{
                          opacity: isDisabled ? 0.4 : 1,
                          cursor: isDisabled ? "not-allowed" : "pointer",
                          pointerEvents: isDisabled ? "none" : "auto",
                          fontSize: "14px",
                          fontWeight: "600",
                        }}
                      >
                        Choose File
                      </label>
                    </div>

                    {isDisabled && (
                      <p
                        className="text-start m-t-5"
                        style={{ color: "var(--primary-25)", fontSize: "11px" }}
                      >
                        Deselect the photo above to upload a new one.
                      </p>
                    )}
                  </div>
                  <div className="col-9 p-0 text-start">
                    {uploadedFile && !isDisabled && (
                      <>
                        <p
                          className="m-b-5 text-success"
                          style={{ fontSize: "11px", overflowWrap: "anywhere" }}
                        >
                          {uploadedFile.name} selected
                        </p>
                        {uploadedPreviewUrl && (
                          <img
                            src={uploadedPreviewUrl}
                            alt={`Preview of ${uploadedFile.name}`}
                            className="d-block"
                            style={{
                              width: "255px",
                              height: "155px",
                              maxWidth: "100%",
                              objectFit: "contain",
                              background: "var(--primary-25)",
                            }}
                          />
                        )}
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer
          className=" d-flex mt-0  justify-content-end"
          style={{ padding: "5px", border: "none" }}
        >
          <Button
            variant="secondary"
            className="btn btn-secondary  height-25 d-flex align-items-center justify-content-center button-padding-footer-new-provider"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            className="btn   height-25 d-flex align-items-center justify-content-center button-padding-footer-new-provider"
            onClick={handleSave}
            disabled={!activeSlot?.id || loading || (!selectedExisting && !uploadedFile)}
          >
            Save
          </Button>
        </Modal.Footer>
      </div>
    </Modal>
  );
};

export default UploadPhotoModal;
